import { computed, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FontsApiService } from '@nebalkon-workspace/browser-api';
import { TranslationsService } from './translations-service';

/**
 * Service that tracks global application readiness state by combining
 * font loading and translation loading states.
 *
 * On the server (SSR), always returns true since skeletons are not needed.
 * On the client, returns true only after both fonts and translations are loaded.
 *
 * @example
 * ```typescript
 * // In a component
 * readonly #appReady = inject(AppReadyService);
 *
 * protected readonly loaded = computed(() =>
 *   this.#appReady.ready() && !!this.dataService.value()
 * );
 * ```
 *
 * @example
 * ```html
 * <div class="card" [class.skeleton]="!loaded()">
 *   <h2>{{ title | shimmerText: 10 }}</h2>
 * </div>
 * ```
 */
@Injectable({ providedIn: 'root' })
export class AppReadyService {
  readonly #fonts = inject(FontsApiService);
  readonly #translations = inject(TranslationsService);
  readonly #isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /**
   * Signal that indicates whether the application is ready to display content.
   *
   * Returns true when:
   * - Running on server (SSR) - skeletons not needed
   * - Running on client AND fonts are loaded AND translations are loaded
   */
  public readonly ready = computed(() => {
    if (!this.#isBrowser) return true;

    const fontsLoaded = this.#fonts.value();
    const translationsLoaded = this.#translations.value();

    return !!fontsLoaded && !!translationsLoaded;
  });
}
